import { Platform, Alert } from 'react-native';
import { Customer, DairyRecord } from '../models/types';
import { formatCurrency, formatNumber, formatDate } from '../utils/helpers';

interface CustomerBillData {
    customer: Customer;
    records: DairyRecord[];
    balance: {
        totalCredit: number;
        totalPaid: number;
        balance: number;
        advance: number;
    };
    dateRange?: {
        from: string;
        to: string;
    };
}

const generateCustomerBillHTML = (data: CustomerBillData): string => {
    const { customer, records, balance, dateRange } = data;
    const typeColor = customer.customerType === 'sale' ? '#4CAF50' : '#FF9800';
    const typeLabel = customer.customerType === 'sale' ? 'BUYER' : 'SUPPLIER';

    // Group records by date
    const recordsByDate: { [date: string]: DairyRecord[] } = {};
    records.forEach(record => {
        const dateKey = record.date;
        if (!recordsByDate[dateKey]) {
            recordsByDate[dateKey] = [];
        }
        recordsByDate[dateKey].push(record);
    });

    // Generate records table rows
    let recordsHTML = '';
    const sortedDates = Object.keys(recordsByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    sortedDates.forEach(date => {
        const dateRecords = recordsByDate[date];
        dateRecords.forEach((record, index) => {
            const timeLabel = record.recordTime === 'morning' ? '🌅 Morning' : '🌙 Evening';
            const statusColor = record.paymentStatus === 'paid' ? '#4CAF50' :
                record.paymentStatus === 'partial' ? '#FF9800' : '#E53935';
            const statusLabel = record.paymentStatus?.toUpperCase() || 'PENDING';

            recordsHTML += `
                <tr>
                    ${index === 0 ? `<td rowspan="${dateRecords.length}" class="date-cell">${formatDate(date)}</td>` : ''}
                    <td>${timeLabel}</td>
                    <td class="right">${formatNumber(record.quantity)} L</td>
                    <td class="right">${formatCurrency(record.pricePerUnit || 0)}</td>
                    <td class="right">${formatCurrency(record.totalAmount || 0)}</td>
                    <td class="right">${formatCurrency(record.amountPaid || 0)}</td>
                    <td class="right" style="color: ${record.pendingAmount && record.pendingAmount > 0 ? '#E53935' : '#4CAF50'}">
                        ${formatCurrency(record.pendingAmount || 0)}
                    </td>
                    <td><span class="status-badge" style="background-color: ${statusColor}20; color: ${statusColor}">${statusLabel}</span></td>
                </tr>
            `;
        });
    });

    if (records.length === 0) {
        recordsHTML = `
            <tr>
                <td colspan="8" class="no-records">No records found for the selected date range</td>
            </tr>
        `;
    }

    const purchaseRecords = records.filter(r => r.recordType === 'purchase');
    const saleRecords = records.filter(r => r.recordType === 'sale');
    const purchaseTotal = purchaseRecords.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
    const saleTotal = saleRecords.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
    const purchasePaid = purchaseRecords.reduce((sum, r) => sum + (r.amountPaid || 0), 0);
    const salePaid = saleRecords.reduce((sum, r) => sum + (r.amountPaid || 0), 0);

    const dateRangeText = dateRange
        ? `${formatDate(dateRange.from)} to ${formatDate(dateRange.to)}`
        : 'All Time';

    const html = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Customer Bill - ${customer.name}</title>
        <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 12px; color: #333; background: #fff; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #eee; }
            .company-name { font-size: 24px; font-weight: bold; color: #2196F3; margin-bottom: 5px; }
            .bill-title { font-size: 16px; color: #666; }
            .customer-section { display: flex; justify-content: space-between; margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; }
            .customer-info h3 { font-size: 18px; color: #333; margin-bottom: 5px; }
            .customer-info p { color: #666; margin-bottom: 3px; }
            .type-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: bold; background-color: ${typeColor}20; color: ${typeColor}; border: 1px solid ${typeColor}; }
            .date-range { text-align: right; }
            .date-range p { color: #666; font-size: 11px; }
            .date-range .range { font-size: 14px; font-weight: 600; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { padding: 10px 8px; text-align: left; border-bottom: 1px solid #eee; }
            th { background: #2196F3; color: white; font-weight: 600; font-size: 11px; text-transform: uppercase; }
            tr:nth-child(even) { background: #f8f9fa; }
            .date-cell { font-weight: 600; background: #e3f2fd !important; vertical-align: top; }
            .right { text-align: right; }
            .status-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
            .no-records { text-align: center; color: #999; padding: 30px; font-style: italic; }
            .summary-section { margin-top: 20px; }
            .summary-title { font-size: 16px; font-weight: bold; color: #333; margin-bottom: 10px; }
            .summary-grid { display: flex; gap: 15px; margin-bottom: 15px; }
            .summary-card { flex: 1; padding: 15px; border-radius: 8px; text-align: center; }
            .summary-card.total { background: #e3f2fd; border: 1px solid #2196F3; }
            .summary-card.paid { background: #e8f5e9; border: 1px solid #4CAF50; }
            .summary-card.pending { background: #ffebee; border: 1px solid #E53935; }
            .summary-card.advance { background: #e8f5e9; border: 1px solid #4CAF50; }
            .summary-card h4 { font-size: 11px; color: #666; margin-bottom: 5px; text-transform: uppercase; }
            .summary-card .value { font-size: 20px; font-weight: bold; }
            .summary-card.total .value { color: #2196F3; }
            .summary-card.paid .value { color: #4CAF50; }
            .summary-card.pending .value { color: #E53935; }
            .summary-card.advance .value { color: #4CAF50; }
            .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #eee; text-align: center; color: #999; font-size: 10px; }
            @media print { body { padding: 0; } }
        </style>
    </head>
    <body>
        <div class="header">
            <div class="company-name">🐄 Amar Dairy Farm</div>
            <div class="bill-title">Customer Statement</div>
        </div>

        <div class="customer-section">
            <div class="customer-info">
                <h3>${customer.name}</h3>
                ${customer.phone ? `<p>📞 ${customer.phone}</p>` : ''}
                ${customer.address ? `<p>📍 ${customer.address}</p>` : ''}
                <span class="type-badge">${typeLabel}</span>
            </div>
            <div class="date-range">
                <p>Report Period</p>
                <div class="range">${dateRangeText}</div>
                <p style="margin-top: 5px;">Generated: ${formatDate(new Date())}</p>
            </div>
        </div>

        <table>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th class="right">Quantity</th>
                    <th class="right">Rate</th>
                    <th class="right">Amount</th>
                    <th class="right">Paid</th>
                    <th class="right">Pending</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                ${recordsHTML}
            </tbody>
        </table>

        <div class="summary-section">
            <div class="summary-title">Account Summary</div>
            <div class="summary-grid">
                <div class="summary-card total">
                    <h4>Total Amount</h4>
                    <div class="value">${formatCurrency(balance.totalCredit)}</div>
                </div>
                <div class="summary-card paid">
                    <h4>Amount Paid</h4>
                    <div class="value">${formatCurrency(balance.totalPaid)}</div>
                </div>
                ${balance.balance > 0 ? `
                <div class="summary-card pending">
                    <h4>Pending Balance</h4>
                    <div class="value">${formatCurrency(balance.balance)}</div>
                </div>
                ` : ''}
                ${balance.advance > 0 ? `
                <div class="summary-card advance">
                    <h4>Advance</h4>
                    <div class="value">${formatCurrency(balance.advance)}</div>
                </div>
                ` : ''}
            </div>
        </div>

        <div class="footer">
            This is a computer-generated statement. For any queries, please contact Amar Dairy Farm.
        </div>
    </body>
    </html>
    `;

    return html;
};

export const generateCustomerBillPDF = async (data: CustomerBillData): Promise<string | null> => {
    // On web, we can't generate PDFs with native modules
    // Instead, open a print dialog
    console.log('PDF generation not available on web, opening print dialog');
    const html = generateCustomerBillHTML(data);

    // Open in new window and trigger print
    const printWindow = window.open('', '_blank');
    if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    }

    return null;
};

export const shareCustomerBillPDF = async (data: CustomerBillData): Promise<boolean> => {
    // On web, we'll open a printable view
    try {
        await generateCustomerBillPDF(data);
        return true;
    } catch (error) {
        console.error('Error generating bill on web:', error);
        Alert.alert('Error', 'PDF generation is not available on web. Please use the mobile app.');
        return false;
    }
};

export default {
    generateCustomerBillPDF,
    shareCustomerBillPDF,
};
